import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  class: "flex flex-col gap-4 text-left",
  "data-testid": "qualification-requirement-tab"
}
const _hoisted_2 = {
  key: 0,
  class: "h-40 rounded-lg bg-gray-100 animate-pulse"
}
const _hoisted_3 = {
  key: 1,
  class: "text-sm text-[#52526f]",
  "data-testid": "qualification-requirement-unavailable"
}
const _hoisted_4 = {
  key: 2,
  class: "text-sm text-negative",
  role: "alert",
  "data-testid": "qualification-requirement-load-error"
}
const _hoisted_5 = {
  class: "text-sm text-[#52526f]",
  "data-testid": "qualification-requirement-current"
}
const _hoisted_6 = { class: "max-w-[260px]" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", { class: "mt-1 text-xs text-gray-500" }, " Plantões antes desta data continuam com a regra anterior. ", -1)

import { computed, onMounted, ref } from 'vue'
import { useToast } from 'vue-toastification'

import Network from '@/Network'
import Input from '@/components/designSystem/Inputs/Input.vue'
import QualificationRequirementSection from '@/components/QualificationRequirement/QualificationRequirementSection.vue'
import {
  clonePolicy,
  earliestValidFrom,
  emptyPolicy,
  validFromError
} from '@/components/QualificationRequirement/policyModel'
import { useErrorToast } from '@/composables/useErrorToast'
import {
  parseBrDate,
  todayInSaoPaulo
} from '@/modules/company/Approvals/ProfessionalDocuments/model'
import type { QualificationRuleGroup, SchedulePolicyLink } from '@/types/qualification-policy.types'
import { getApiErrorMessage } from '@/utils/apiError'
import { formatIsoDateOnlyBR } from '@/utils/date'
import { isProfessionalDocumentsDisabled } from '@/utils/qualificationErrors'


export default /*#__PURE__*/_defineComponent({
  __name: 'QualificationRequirementTab',
  props: {
    scheduleId: {},
    companyId: {}
  },
  emits: ["update:isSaving"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;




const toast = useToast()
const { notifyError } = useErrorToast()

const isLoading = ref(false)
const isUnavailable = ref(false)
const loadError = ref<string | null>(null)
const current = ref<SchedulePolicyLink | null>(null)
const history = ref<SchedulePolicyLink[]>([])
const policy = ref<QualificationRuleGroup>(emptyPolicy())
const originalSnapshot = ref(JSON.stringify(emptyPolicy()))
const isPolicyValid = ref(true)
/** Preenchido pelo "Copiar requisitos" da mesma empresa; vai no PUT e zera a cada carga. */
const copiedFromPolicyId = ref<number | null>(null)
const validFrom = ref('')
const validFromMessage = ref<string | null>(null)

/** Vínculo aberto (vigente ou agendado): é a versão editada, como o backend a substitui. */
const openLink = computed(() => history.value.find((link) => link.validTo === null) ?? null)
const scheduled = computed(() =>
  openLink.value && openLink.value.validFrom > todayInSaoPaulo() ? openLink.value : null
)
const previousCount = computed(() => {
  const today = todayInSaoPaulo()
  return history.value.filter((link) => link.validTo !== null && link.validTo < today).length
})

const statusText = computed(() => {
  const link = current.value
  const parts = [
    link
      ? `Vigente desde ${formatIsoDateOnlyBR(link.validFrom)}${link.validTo ? ` até ${formatIsoDateOnlyBR(link.validTo)}` : ''}.`
      : 'Esta escala ainda não exige qualificação.'
  ]
  if (scheduled.value)
    parts.push(
      `Nova versão a partir de ${formatIsoDateOnlyBR(scheduled.value.validFrom)}: é a que aparece abaixo.`
    )
  const count = previousCount.value
  if (count)
    parts.push(`${count} ${count === 1 ? 'versão anterior' : 'versões anteriores'} no histórico.`)
  return parts.join(' ')
})

const hasUnsavedChanges = computed(() => JSON.stringify(policy.value) !== originalSnapshot.value)
const isFormValid = computed(
  () => !isLoading.value && !isUnavailable.value && !loadError.value && isPolicyValid.value
)

async function load() {
  isLoading.value = true
  loadError.value = null
  try {
    const response = await Network.qualificationEligibility.getSchedulePolicy(props.scheduleId)
    current.value = response.current
    history.value = response.history
    policy.value = openLink.value ? clonePolicy(openLink.value.rules) : emptyPolicy()
    originalSnapshot.value = JSON.stringify(policy.value)
    copiedFromPolicyId.value = null
    validFrom.value = formatIsoDateOnlyBR(earliestValidFrom(response.history, todayInSaoPaulo()))
    validFromMessage.value = null
  } catch (error) {
    if (isProfessionalDocumentsDisabled(error)) {
      // fallback intencional: feature desligada no backend (R46.4) — estado próprio, sem toast.
      isUnavailable.value = true
      return
    }
    console.error('Erro ao carregar a qualificação exigida da escala:', error)
    loadError.value = getApiErrorMessage(
      error,
      'Não foi possível carregar a qualificação exigida da escala'
    )
  } finally {
    isLoading.value = false
  }
}

const onValidityChange = (valid: boolean) => (isPolicyValid.value = valid)

/** R51.4: a linhagem só vale na mesma empresa — o backend recusa origem de outra (404). */
function onCopiedFrom(sourcePolicyId: number, sourceCompanyId: number | null) {
  copiedFromPolicyId.value = sourceCompanyId === props.companyId ? sourcePolicyId : null
}

function onValidFromInput(value: string) {
  validFrom.value = value
  validFromMessage.value = null
}

/** Salvar = fechar o vínculo aberto e abrir outro a partir de `validFrom` (D61). Raiz vazia remove a exigência. */
async function handleSave(): Promise<void> {
  if (!isFormValid.value) return
  const today = todayInSaoPaulo()
  validFromMessage.value = validFromError(
    validFrom.value,
    today,
    earliestValidFrom(history.value, today)
  )
  const validFromIso = parseBrDate(validFrom.value)
  if (validFromMessage.value || !validFromIso) return
  emit('update:isSaving', true)
  try {
    await Network.qualificationEligibility.putSchedulePolicy(props.scheduleId, {
      rules: policy.value,
      validFrom: validFromIso,
      ...(copiedFromPolicyId.value !== null ? { copiedFromPolicyId: copiedFromPolicyId.value } : {})
    })
    toast.success(
      `Qualificação exigida salva. Vale para plantões a partir de ${formatIsoDateOnlyBR(validFromIso)}.`
    )
    await load()
  } catch (error) {
    notifyError(error, 'Não foi possível salvar a qualificação exigida da escala')
  } finally {
    emit('update:isSaving', false)
  }
}

onMounted(load)

__expose({ handleSave, hasUnsavedChanges, isFormValid })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2))
      : (isUnavailable.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_3, " A qualificação exigida ainda não foi liberada neste ambiente. "))
        : (loadError.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(loadError.value), 1))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              _createElementVNode("p", _hoisted_5, _toDisplayString(statusText.value), 1),
              _createVNode(QualificationRequirementSection, {
                modelValue: policy.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((policy).value = $event)),
                onValidityChange: onValidityChange,
                onCopiedFrom: onCopiedFrom
              }, null, 8, ["modelValue"]),
              _createElementVNode("div", _hoisted_6, [
                _createVNode(Input, {
                  label: "Vale para plantões a partir de",
                  required: "",
                  mask: "##/##/####",
                  width: "100%",
                  value: validFrom.value,
                  "is-error": !!validFromMessage.value,
                  "error-message": validFromMessage.value ?? '',
                  "data-testid": "qualification-requirement-valid-from",
                  "onUpdate:modelValue": onValidFromInput
                }, null, 8, ["value", "is-error", "error-message"]),
                _hoisted_7
              ])
            ], 64))
  ]))
}
}

})